import { formatPhoneNumber } from "./phoneFormat";

/**
 * Sorting function for objects with a name property
 */
const sortByName = <T extends { name?: string | null }>(a: T, b: T): number => {
  if (!a.name || !b.name) {
    return 0;
  }

  return a.name.localeCompare(b.name);
};

/**
 * Format an integer or float as a currency
 * @param cost - number representing cost
 * @param currency - currency type (e.g. "USD", "AUS", etc.)
 * @example 125.5 -> "$125.50"
 */
const formatCost = (cost: number, currency: string = "USD"): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(cost);
};

const dollarsToCents = (cost: number): number => {
  if (cost === undefined || cost === null) {
    return 0;
  }
  if (typeof cost === "string") {
    cost = parseFloat(cost);
  }

  if (isNaN(cost)) {
    return 0;
  }

  return Math.round(cost * 100);
};

const centsToDollars = (cost: number): number => {
  if (cost === undefined || cost === null) {
    return 0;
  }
  if (typeof cost === "string") {
    cost = parseFloat(cost);
  }
  if (isNaN(cost)) {
    return 0;
  }
  return Math.round(cost / 100);
};

/**
 * Format a fixed-point representation of a dollar value as a float
 * @param cost - number representing cost
 * @param currency - currency type (e.g. "USD", "AUS", etc.)
 * @example 8525 -> "$85.25"
 */
const formatRawCost = (cost: number, currency: string = "USD"): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(cost / 100);
};

/**
 * Map {"Y" | "N" | Fasley} to {true | false | null}
 */
const mapPseudoBool = (value: string | null | undefined): boolean | null => {
  if (value === "Y") return true;
  if (value === "N") return false;
  return null;
};

/**
 * Map {true | false | Fasley} to {"Y" | "N" | null}
 */
const unMapPseudoBool = (value: boolean | null | undefined): string | null => {
  if (value === true) return "Y";
  if (value === false) return "N";
  return null;
};

const shallowDiff = (obj1: Record<string, any>, obj2: Record<string, any>) => {
  const diff: Record<string, any> = {};

  Object.keys(obj1).forEach((key) => {
    if (!(key in obj2)) {
      diff[key] = { old: obj1[key], new: undefined }; // Removed key
    } else if (obj1[key] !== obj2[key]) {
      diff[key] = { old: obj1[key], new: obj2[key] }; // Changed key
    }
  });

  Object.keys(obj2).forEach((key) => {
    if (!(key in obj1)) {
      diff[key] = { old: undefined, new: obj2[key] }; // Added key
    }
  });

  return diff;
};

/**
 * Asnychronous delay function
 * @param {number} ms - milliseconds to delay
 */
const delay = (ms: number) => {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
};

/**
 * Returns an ISO 8601–formatted string representing the given Date
 * in local time, without the timezone offset.
 *
 * @param date - The Date object to be converted into a local ISO string.
 * @returns A string in the format of "YYYY-MM-DDTHH:mm:ss".
 */
const toLocalISOString = (date: Date) => {
  const pad = (num: number) => num.toString().padStart(2, "0"); // Ensures two-digit formatting

  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
};

/**
 * Converts a Date object into a string in the format YYYY-MM-DD.
 *
 * @param date - The Date object to convert.
 * @returns A string representing the local date in YYYY-MM-DD format.
 */
const toLocalDateString = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // getMonth() returns 0-based month
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/**
 * Special HTML characters
 */
enum SpecialCharacters {
  Infinity = "∞",
  EmDash = "—",
  EnDash = "–",
  DoublyHyphen = "--",
}

function toLocalISODateString(date: Date) {
  const pad = (num: number) => num.toString().padStart(2, "0"); // Ensures two-digit formatting
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Convert local ISO string to date object
 * @param dateString - Local ISO string
 * @returns Date object
 */
function dateStringToDate(dateString: string): Date {
  const [datePart, timePart] = dateString.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hours, minutes, seconds] = timePart.split(":").map(Number);

  return new Date(year, month - 1, day);
}

const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

function formatDateNY(
  dateString: string | Date | null | undefined,
): string | null {
  try {
    if (!dateString) return null;
    // if date only, add in time set to day start
    const date =
      typeof dateString == "string"
        ? dateRegex.test(dateString)
          ? new Date(`${dateString}T00:00:00`)
          : new Date(dateString)
        : dateString;

    return new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      year: "numeric",
      month: "numeric",
      day: "numeric",
    }).format(date);
  } catch (error) {
    console.error("formatDateNY error");
    console.error(error);
    return null;
  }
}

function formatTimeNY(
  dateString: string | Date | null | undefined,
  plusMinutes?: number,
): string | null {
  if (!dateString) return null;
  try {
    // if date only, add in time set to day start
    const date =
      typeof dateString == "string"
        ? dateRegex.test(dateString)
          ? new Date(`${dateString}T00:00:00`)
          : new Date(dateString)
        : dateString;
    if (plusMinutes) {
      date.setMinutes(date.getMinutes() + plusMinutes);
    }
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    }).format(date);
  } catch (error) {
    console.error("formatTimeNY error");
    console.error(error);
    return null;
  }
}

/**
 * calculate age from date of birth
 * @param dob - Date of birth
 * @returns age
 */
const calculateAge = (dob: Date) => {
  const diff_ms = Date.now() - dob.getTime();
  const age_dt = new Date(diff_ms);

  return Math.abs(age_dt.getUTCFullYear() - 1970);
};

// used to take in date how it is stored in the current db and return it as the proper date object
function localTimeStringToDate(input: string): Date | null {
  try {
    // Match "YYYY-MM-DDTHH:mm"
    const dateTimeMatch = input.match(
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/,
    );
    if (dateTimeMatch) {
      const [_, yearStr, monthStr, dayStr, hourStr, minuteStr] = dateTimeMatch;
      const year = Number(yearStr);
      const month = Number(monthStr);
      const day = Number(dayStr);
      const hour = Number(hourStr);
      const minute = Number(minuteStr);

      if (
        month < 1 ||
        month > 12 ||
        day < 1 ||
        day > 31 ||
        hour < 0 ||
        hour > 23 ||
        minute < 0 ||
        minute > 59
      ) {
        throw new Error("DateTime components out of valid range.");
      }

      return new Date(year, month - 1, day, hour, minute);
    }

    // Match "YYYY-MM-DD"
    const dateOnlyMatch = input.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const [_, yearStr, monthStr, dayStr] = dateOnlyMatch;
      const year = Number(yearStr);
      const month = Number(monthStr);
      const day = Number(dayStr);

      if (month < 1 || month > 12 || day < 1 || day > 31) {
        throw new Error("Date components out of valid range.");
      }

      return new Date(year, month - 1, day);
    }

    throw new Error(
      "Invalid format. Expected 'YYYY-MM-DD' or 'YYYY-MM-DDTHH:mm'.",
    );
  } catch (e) {
    console.error(e);
    return null;
  }
}

function genFullName(
  nameFirst: string | null,
  nameLast: string | null,
): string {
  if (nameFirst && nameLast) {
    return `${nameFirst} ${nameLast}`;
  } else if (nameFirst) {
    return nameFirst;
  } else if (nameLast) {
    return nameLast;
  } else {
    return "";
  }
}

const stripNonNumbers = (str: string) => str.replace(/\D/g, "");

/**
 * Check if the current scope is within a component
 * @returns boolean
 */
const inComponent = () => {
  return getCurrentInstance() != null;
};

const stringMaxLength = (
  value: string | undefined | null,
  maxLength: number,
) => {
  if (!value) return value;
  const shortenedIfNeeded = value.substring(
    0,
    Math.min(maxLength - 3, value.length),
  );
  if (shortenedIfNeeded.length != value.length)
    return shortenedIfNeeded + "...";
  return value;
};

const numberToOrdinal = (n: number): string => {
  if (n >= 11 && n <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
};

/**
 * Legacy membership label: `Name (Until: MM/dd/yyyy)`, or `Name (Lifetime)` without an
 * expiration. Null when there is no active membership.
 */
const formatMembershipLabel = (person: {
  activeMembershipTypeName?: string | null;
  activeMembershipExpiration?: Date | null;
}): string | null => {
  if (!person.activeMembershipTypeName) return null;
  if (!person.activeMembershipExpiration)
    return `${person.activeMembershipTypeName} (Lifetime)`;
  const until = person.activeMembershipExpiration.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
  return `${person.activeMembershipTypeName} (Until: ${until})`;
};

export const useUtility = () => {
  return {
    stripNonNumbers,
    formatPhoneNumber,
    formatRawCost,
    formatCost,
    dollarsToCents,
    centsToDollars,
    sortByName,
    mapPseudoBool,
    unMapPseudoBool,
    shallowDiff,
    delay,
    toLocalISOString,
    dateStringToDate,
    toLocalISODateString,
    toLocalDateString,
    calculateAge,
    SpecialCharacters,
    formatDateNY,
    formatTimeNY,
    localTimeStringToDate,
    genFullName,
    inComponent,
    numberToOrdinal,
    stringMaxLength,
    formatMembershipLabel,
  };
};
